import { ArrowLeft, CalendarDays, MapPin, Table2, UserRoundSearch, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { loadAuthorizedReportData, type WebReportData } from "../features/reports/report-web-data";
import {
  REPORT_DEFINITIONS,
  generateReportData,
  type ReportScopeOptions,
} from "../features/desktop-add-new-data/modules/reports/report-calculations";
import {
  getReportAgeBrackets,
  getResidentsForReportAgeBracket,
} from "../features/reports/report-age-names";

export function ReportAgeNamesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { reportId: rawReportId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<WebReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reportId = parseReportId(rawReportId);
  const municipality = searchParams.get("municipality") ?? municipalityName(user?.locationLabel ?? "");
  const scopeOptions: ReportScopeOptions = {
    municipality,
    municipalityWide: searchParams.get("scope") === "municipality",
    barangay: searchParams.get("scope") === "municipality"
      ? undefined
      : searchParams.get("barangay") ?? undefined,
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadAuthorizedReportData()
      .then((result) => {
        if (!active) return;
        setData(result);
        setError("");
      })
      .catch(() => { if (active) setError("Unable to load the names for this report right now."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const reportData = useMemo(() => generateReportData(
    reportId,
    scopeOptions,
    data?.residents ?? [],
    data?.households ?? [],
  ), [data, reportId, scopeOptions.barangay, scopeOptions.municipality, scopeOptions.municipalityWide]);

  const ageBrackets = useMemo(() => getReportAgeBrackets(reportData), [reportData]);
  const requestedAge = searchParams.get("age") ?? "";
  const selectedAge = ageBrackets.includes(requestedAge) ? requestedAge : ageBrackets[0] ?? "";
  const residents = useMemo(
    () => selectedAge
      ? getResidentsForReportAgeBracket(
          reportId,
          selectedAge,
          data?.residents ?? [],
          data?.households ?? [],
          scopeOptions,
        )
      : [],
    [data, reportId, scopeOptions.barangay, scopeOptions.municipality, scopeOptions.municipalityWide, selectedAge],
  );

  if (!ageBrackets.length) {
    return <Navigate to={buildReportPath(reportId, scopeOptions)} replace />;
  }

  const scopeLabel = scopeOptions.municipalityWide
    ? municipality || "All Barangays"
    : scopeOptions.barangay || reportData.scopeLabel;

  return <section className="desktop-report-names-page">
    <header className="desktop-report-detail-hero desktop-report-names-hero">
      <button type="button" className="desktop-report-back" onClick={() => navigate(buildReportPath(reportId, scopeOptions))}><ArrowLeft />Back to Report</button>
      <div className="desktop-report-detail-title desktop-report-names-title"><div className="desktop-report-hero-icon"><UserRoundSearch /></div><div><span>AGE BRACKET NAMES</span><h1>Table {reportData.reportId}. {reportData.title}</h1><p>{scopeOptions.municipalityWide ? "All Barangays" : "Barangay"}: {scopeLabel}</p></div></div>
    </header>

    {error ? <div className="form-error" role="alert">{error}</div> : null}

    <section className="desktop-report-meta-grid">
      <MetaCard icon={<Table2 />} label="Report" value={`Table ${reportData.reportId}`} helper="Selected report age row" />
      <MetaCard icon={<MapPin />} label="Scope" value={scopeLabel} helper={scopeOptions.municipalityWide ? "Municipality-wide" : "Barangay"} />
      <MetaCard icon={<Users />} label="Names Shown" value={loading ? "…" : residents.length.toLocaleString()} helper={`Age ${selectedAge}`} />
      <MetaCard icon={<CalendarDays />} label="Date Created" value={formatDate(reportData.generatedAt)} helper="Submitted records only" />
    </section>

    <section className="desktop-report-names-control">
      <div><strong>Selected age bracket</strong><p>Only individuals who contribute to this age row in the selected report are shown. The report's own filters and conditions are applied.</p></div>
      <div className="desktop-report-fixed-age"><span>Age bracket</span><strong>{selectedAge}</strong></div>
    </section>

    <section className="desktop-report-names-card">
      <div className="desktop-report-names-card-heading"><div><span>AGE {selectedAge || "—"}</span><h2>Individuals included in this report row</h2><p>{scopeOptions.municipalityWide ? `${municipality} · All Barangays` : scopeLabel} · submitted records only</p></div><strong>{loading ? "Loading…" : `${residents.length.toLocaleString()} name${residents.length === 1 ? "" : "s"}`}</strong></div>
      {loading ? <div className="desktop-report-loading">Loading names…</div> : residents.length ? <div className="desktop-report-names-table-wrap"><table className="desktop-report-names-table"><thead><tr><th>#</th><th>Name</th><th>Age</th><th>Sex</th><th>Household</th><th>Family</th><th>Migration Status</th><th>Barangay</th></tr></thead><tbody>{residents.map((resident, index) => <tr key={resident.residentId}><td>{index + 1}</td><td><strong>{resident.fullName || "Unnamed resident"}</strong></td><td>{resident.ageDisplay || (resident.ageYears ?? "—")}</td><td>{resident.sex || "—"}</td><td>{resident.householdCode || "—"}</td><td>{resident.familyName || resident.familyUnitNo || "—"}</td><td>{resident.migrationStatus || "—"}</td><td>{resident.barangay || "—"}</td></tr>)}</tbody></table></div> : <div className="desktop-report-empty"><strong>No names in this report row.</strong><br />No submitted residents in {scopeOptions.municipalityWide ? "the municipality" : scopeLabel} contribute to age {selectedAge} in this report.</div>}
    </section>
  </section>;
}

function MetaCard({ icon, label, value, helper }: { icon: React.ReactNode; label: string; value: string; helper: string }) {
  return <article className="desktop-report-meta-card"><div>{icon}</div><section><span>{label}</span><strong>{value}</strong><p>{helper}</p></section></article>;
}

function buildReportPath(reportId: number, scope: ReportScopeOptions) {
  const params = new URLSearchParams();
  if (scope.municipality) params.set("municipality", scope.municipality);
  if (scope.municipalityWide) params.set("scope", "municipality");
  else {
    params.set("scope", "barangay");
    if (scope.barangay) params.set("barangay", scope.barangay);
  }
  return `/reports/${reportId}?${params.toString()}`;
}

function parseReportId(value: string | undefined) {
  const id = Number(value);
  return REPORT_DEFINITIONS.some((definition) => definition.id === id) ? id : 1;
}
function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }).format(date);
}
function municipalityName(locationLabel: string) { return locationLabel.split(",")[0]?.trim() ?? ""; }
